import { useState, useEffect } from 'react';

interface ExchangeRates {
  EUR: number;
  USD: number;
}

interface UseExchangeRateReturn {
  rates: ExchangeRates;
  loading: boolean;
  error: string | null;
  convert: (amount: number, fromCurrency: string, toCurrency: string) => number;
}

// Cache the rates for 1 hour
const CACHE_KEY = 'exchangeRates';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in ms

interface CachedRates {
  rates: ExchangeRates;
  timestamp: number;
}

const getCachedRates = (): ExchangeRates | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const parsed: CachedRates = JSON.parse(cached);
    if (Date.now() - parsed.timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed.rates;
  } catch {
    return null;
  }
};

const setCachedRates = (rates: ExchangeRates) => {
  const cached: CachedRates = {
    rates,
    timestamp: Date.now(),
  };
  localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
};

export const useExchangeRate = (): UseExchangeRateReturn => {
  const [rates, setRates] = useState<ExchangeRates>({ EUR: 1, USD: 1.08 }); // Default fallback
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRates = async () => {
      // Check cache first
      const cached = getCachedRates();
      if (cached) {
        setRates(cached);
        setLoading(false);
        return;
      }

      try {
        // Frankfurter API - free, no API key, ECB data
        const response = await fetch('https://api.frankfurter.app/latest?from=EUR&to=USD');
        
        if (!response.ok) {
          throw new Error('Failed to fetch exchange rates');
        }
        
        const data = await response.json();
        const newRates: ExchangeRates = {
          EUR: 1,
          USD: data.rates.USD,
        };
        
        setRates(newRates);
        setCachedRates(newRates);
        setError(null);
      } catch (err) {
        console.error('Exchange rate fetch error:', err);
        setError('Could not fetch live rates');
        // Keep using fallback rates
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
  }, []);

  const convert = (amount: number, fromCurrency: string, toCurrency: string): number => {
    const from = fromCurrency.toUpperCase();
    const to = toCurrency.toUpperCase();
    
    if (from === to) return amount;
    
    // Convert to EUR first (base currency)
    let amountInEur = amount;
    if (from === 'USD') {
      amountInEur = amount / rates.USD;
    }
    
    // Convert from EUR to target
    if (to === 'USD') {
      return amountInEur * rates.USD;
    }
    
    return amountInEur;
  };

  return { rates, loading, error, convert };
};
